/**
 * localStorage, best-effort.
 *
 * Every call is wrapped: private-browsing modes throw on read as well as write, and storage can be
 * disabled or full. A caller must treat a miss as "not stored", never as an error - a session that
 * lasts only until reload is a far better outcome than a sign-in page that refuses to work.
 *
 * The same contract the OpenSilver app's `BrowserStorage` has, and the same one `api/auth.ts`
 * depends on for the refresh token.
 */
export function read(key: string): string | null {
  try {
    const value = localStorage.getItem(key);
    return value === null || value === '' ? null : value;
  } catch {
    return null;
  }
}

export function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    /* see the module comment */
  }
}

export function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    /* see the module comment */
  }
}

/** sessionStorage, under the same best-effort contract: per tab, gone when the tab closes. */
export function readSession(key: string): string | null {
  try {
    const value = sessionStorage.getItem(key);
    return value === null || value === '' ? null : value;
  } catch {
    return null;
  }
}

export function writeSession(key: string, value: string): void {
  try {
    sessionStorage.setItem(key, value);
  } catch {
    /* see the module comment */
  }
}

export function removeSession(key: string): void {
  try {
    sessionStorage.removeItem(key);
  } catch {
    /* see the module comment */
  }
}

/** Whether storage can be written at all - so a miss can be told from a browser that keeps nothing. */
export function usable(): boolean {
  try {
    localStorage.setItem('jetsoncrm.probe', '1');
    localStorage.removeItem('jetsoncrm.probe');
    return true;
  } catch {
    return false;
  }
}
